import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Botao } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { get } from '../../nucleo/api';
import { data, dinheiro, textoLivre } from '../../nucleo/formato';
import { descricaoGerada } from '../../nucleo/texto-sistema';
import { NomeParceiro, Valor } from './comum';
import type { Detalhe as DetalheApi, ItemDetalhe } from './tipos';
import { rotuloCategoria } from '../../nucleo/rotulos';

/**
 * Drill de um número do relatório até o lançamento (e, na competência, até o documento do Contas): as linhas que o formam,
 * com a soma do filtro inteiro calculada no servidor (confere com o número clicado) e o aviso quando passa de 500 linhas.
 */
export function Detalhe({ titulo, caminho, params, aoFechar }: { titulo: string; caminho: string; params: Record<string, unknown>; aoFechar: () => void }) {
  const { t } = useTranslation('relatorios');
  const { t: tn } = useTranslation();
  const q = useQuery({ queryKey: [caminho, params], queryFn: () => get<DetalheApi>(caminho, { ...params, limite: 500 }) });
  const link = (i: ItemDetalhe) => {
    if (i.origem === 'documento' && i.registro_id) return `/financeiro/contas/${i.valor_centavos >= 0 ? 'a-receber' : 'a-pagar'}?estado=todos${i.parceiro_id ? `&parceiro_id=${i.parceiro_id}` : ''}`;
    if (i.origem === 'lancamento') return `/financeiro/financeiro/entradas-saidas?de=${i.data}&ate=${i.data}${i.parceiro_id ? `&parceiro_id=${i.parceiro_id}` : ''}`;
    return null;
  };
  return (
    <Modal
      aberto
      largura="lg"
      aoFechar={aoFechar}
      titulo={t('detalhe.titulo', { nome: titulo })}
      rodape={
        <Botao variante="fantasma" onClick={aoFechar}>
          {tn('comum.fechar')}
        </Botao>
      }
    >
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <p className="text-sm text-tinta-3">{t('detalhe.vazio')}</p>
          ) : (
            <div className="flex min-w-0 flex-col gap-3">
              <p className="text-sm text-tinta-2">
                {d.soma_por_moeda.map((s) => (
                  <span key={s.moeda} className="mr-3">
                    {t('detalhe.soma', { valor: dinheiro(s.valor_centavos, s.moeda) })}
                  </span>
                ))}
              </p>
              <div tabIndex={0} role="region" aria-label={t('comum.tabela')} className="max-h-96 overflow-auto rounded-lg border border-borda focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-sup-1">
                    <tr className="border-b border-borda text-left text-tinta-3">
                      <th scope="col" className="px-3 py-2 font-medium">{t('detalhe.data')}</th>
                      <th scope="col" className="px-3 py-2 font-medium">{t('detalhe.descricao')}</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">{t('detalhe.valor')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.itens.map((i) => {
                      const destino = link(i);
                      return (
                        <tr key={`${i.origem}-${i.id}`} className="border-b border-borda last:border-0">
                          <td className="numero px-3 py-2 whitespace-nowrap text-tinta-2">{data(i.data)}</td>
                          <td className="max-w-0 px-3 py-2">
                            <TextoCortado className="block">{i.numero ? `${i.numero} ${textoLivre(i.descricao, i.parceiro_nome)}`.trim() : textoLivre(descricaoGerada(i.descricao), i.parceiro_nome) || t(`detalhe.origem.${i.origem}`)}</TextoCortado>
                            <span className="block text-xs break-words text-tinta-3">
                              {[t(`detalhe.origem.${i.origem}`), i.componente && i.componente !== 'principal' ? t(`detalhe.componente.${i.componente}`) : null, rotuloCategoria(i.categoria_nome)].filter(Boolean).join(', ')}
                              {i.parceiro_id && (
                                <>
                                  {', '}
                                  <NomeParceiro nome={i.parceiro_nome} vazio={t('comum.semNome')} />
                                </>
                              )}
                              {destino && (
                                <>
                                  {' · '}
                                  <Link to={destino} className="text-acento-forte underline-offset-2 hover:underline">
                                    {i.origem === 'documento' ? t('detalhe.abrirDocumento', { numero: i.numero ?? '' }) : t('detalhe.abrirLancamentos')}
                                  </Link>
                                </>
                              )}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Valor centavos={i.valor_centavos} moeda={i.moeda} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {d.truncado && <p className="text-xs text-tinta-3">{t('detalhe.mostrando', { n: d.itens.length, total: d.total })}</p>}
            </div>
          )
        }
      </Estado>
    </Modal>
  );
}
